// ./src/utils/api/PartnerPackageService.ts
//
// PartnerPackage (account-level reusable partner/sponsor offer catalog) service.
// OpResult-native. Account-owned: every read is scoped to the caller's account
// and only the caller's own packages are editable (see the `rights` field). A
// multi-id update/remove is a bulk op (server restricts a bulk update to status).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants — mirror the eventsmagic-api partner boundary (src/boundary/partner.ts)
// =============================================================================

/** What kind of partner an offer targets. */
export const PARTNER_KIND = {
  SPONSOR: "sponsor",
  PARTNER: "partner",
  MEDIA_PARTNER: "media_partner",
  EXHIBITOR: "exhibitor",
  SERVICE_VENDOR: "service_vendor",
  COMMUNITY_PARTNER: "community_partner",
  OTHER: "other",
} as const;

export type PartnerKind = (typeof PARTNER_KIND)[keyof typeof PARTNER_KIND];

export const PARTNER_KIND_VALUES: PartnerKind[] = Object.values(PARTNER_KIND);

/** Which way money flows for a package. */
export const PARTNER_MONEY_DIRECTION = {
  REVENUE: "revenue",
  EXPENSE: "expense",
  NONE: "none",
} as const;

export type PartnerMoneyDirection = (typeof PARTNER_MONEY_DIRECTION)[keyof typeof PARTNER_MONEY_DIRECTION];

export const PARTNER_MONEY_DIRECTION_VALUES: PartnerMoneyDirection[] = Object.values(PARTNER_MONEY_DIRECTION);

/**
 * Human-readable English labels for each partner kind, used as the i18n
 * `defaultValue` so codes ("media_partner") never surface in the UI. Translators
 * override via the `partnerPackages:kind.<code>` keys.
 */
export const PARTNER_KIND_LABELS: Record<PartnerKind, string> = {
  [PARTNER_KIND.SPONSOR]: "Sponsor",
  [PARTNER_KIND.PARTNER]: "Partner",
  [PARTNER_KIND.MEDIA_PARTNER]: "Media partner",
  [PARTNER_KIND.EXHIBITOR]: "Exhibitor",
  [PARTNER_KIND.SERVICE_VENDOR]: "Service vendor",
  [PARTNER_KIND.COMMUNITY_PARTNER]: "Community partner",
  [PARTNER_KIND.OTHER]: "Other",
};

export const partnerKindLabel = (kind?: string | null): string =>
  PARTNER_KIND_LABELS[kind as PartnerKind] ?? kind ?? "";

/** Human-readable English labels for each money direction (see PARTNER_KIND_LABELS). */
export const PARTNER_MONEY_DIRECTION_LABELS: Record<PartnerMoneyDirection, string> = {
  [PARTNER_MONEY_DIRECTION.REVENUE]: "Revenue",
  [PARTNER_MONEY_DIRECTION.EXPENSE]: "Expense",
  [PARTNER_MONEY_DIRECTION.NONE]: "No money",
};

export const partnerMoneyLabel = (dir?: string | null): string =>
  PARTNER_MONEY_DIRECTION_LABELS[dir as PartnerMoneyDirection] ?? dir ?? "";

/** Per-kind display metadata (icon + accent color) for badges and list rows. */
export const PARTNER_KIND_META: Record<PartnerKind, { icon: string; color: string }> = {
  [PARTNER_KIND.SPONSOR]: { icon: "Award", color: "yellow" },
  [PARTNER_KIND.PARTNER]: { icon: "HeartHandshake", color: "grape" },
  [PARTNER_KIND.MEDIA_PARTNER]: { icon: "DeviceTv", color: "blue" },
  [PARTNER_KIND.EXHIBITOR]: { icon: "Building", color: "teal" },
  [PARTNER_KIND.SERVICE_VENDOR]: { icon: "Tool", color: "orange" },
  [PARTNER_KIND.COMMUNITY_PARTNER]: { icon: "Users", color: "green" },
  [PARTNER_KIND.OTHER]: { icon: "Star", color: "gray" },
};

export const partnerKindMeta = (kind?: string | null): { icon: string; color: string } =>
  PARTNER_KIND_META[(kind as PartnerKind) ?? PARTNER_KIND.OTHER] ?? PARTNER_KIND_META[PARTNER_KIND.OTHER];

// =============================================================================
// GraphQL
// =============================================================================

export const PARTNER_PACKAGE_FIELDS = `
  id
  accountId
  partnerKind
  name
  description
  benefits
  defaultAmount
  currencyCode
  moneyDirection
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const PARTNER_PACKAGE_LIST_QUERY = gql`
  query PartnerPackageList($filter: PartnerPackageFilter, $params: PaginationAndSorting) {
    partnerPackageList(filter: $filter, params: $params) {
      code
      data { ${PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PACKAGE_GET_QUERY = gql`
  query PartnerPackageGet($id: ID) {
    partnerPackageGet(id: $id) {
      code
      data { ${PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PACKAGE_GET_MANY_QUERY = gql`
  query PartnerPackageGetMany($ids: [ID]) {
    partnerPackageGetMany(ids: $ids) {
      code
      data { ${PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PACKAGE_CREATE_MUTATION = gql`
  mutation PartnerPackageCreate($params: PartnerPackageInput) {
    partnerPackageCreate(params: $params) {
      code
      data { ${PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PACKAGE_UPDATE_MUTATION = gql`
  mutation PartnerPackageUpdate($where: PartnerPackageWhereInput, $params: PartnerPackageInput) {
    partnerPackageUpdate(where: $where, params: $params) {
      code
      data { ${PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PARTNER_PACKAGE_REMOVE_MUTATION = gql`
  mutation PartnerPackageRemove($where: PartnerPackageWhereInput) {
    partnerPackageRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api PartnerPackage schema)
// =============================================================================

export interface PartnerPackage {
  id?: string;
  accountId?: string | null;
  /** sponsor | partner | media_partner | exhibitor | service_vendor | community_partner | other. */
  partnerKind?: string | null;
  /** e.g. "Gold Sponsor", "3x3 Booth", "Catering Slot". */
  name?: string | null;
  description?: string | null;
  /** List of what the package includes. */
  benefits?: string[] | null;
  /** Default price (overridable per event). */
  defaultAmount?: number | null;
  currencyCode?: string | null;
  /** revenue | expense | none. */
  moneyDirection?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PartnerPackageInput {
  partnerKind?: string | null;
  name?: string | null;
  description?: string | null;
  benefits?: string[] | null;
  defaultAmount?: number | null;
  currencyCode?: string | null;
  moneyDirection?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /**
   * Authorization context (NOT stored): the event Partners page the caller is acting from. Lets crew with
   * event.operate on it create/edit a package under the event's account. Omit on the Library page.
   */
  eventId?: string | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface PartnerPackageBulkInput {
  status?: number | null;
}

export interface PartnerPackageFilter {
  id?: string[] | null;
  status?: number[] | null;
  partnerKind?: string[] | null;
  moneyDirection?: string[] | null;
  searchKeyword?: string | null;
  /**
   * Authorization/scoping context (NOT a column): the event Partners page the list is shown on. Scopes
   * reads to that event's account for authorized crew (event.operate), so a co_host sees the packages.
   */
  eventId?: string | null;
}

export interface PartnerPackageWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
  /** Authorization context for a crew remove — the event Partners page the delete is issued from. NOT stored. */
  eventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class PartnerPackageService {
  static async list(
    filter?: PartnerPackageFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(PARTNER_PACKAGE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PARTNER_PACKAGE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(PARTNER_PACKAGE_GET_MANY_QUERY, { ids });
  }

  static async create(params: PartnerPackageInput): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PACKAGE_CREATE_MUTATION, { params });
  }

  static async update(where: PartnerPackageWhereInput, params: PartnerPackageInput): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PACKAGE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(PARTNER_PACKAGE_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-change status on every selected package in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: PartnerPackageBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(PARTNER_PACKAGE_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected package in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(PARTNER_PACKAGE_REMOVE_MUTATION, { where: { id: ids } });
  }
}
